'use client'

import {
  Bar,
  BarChart as BarCharRecharts,
  CartesianGrid,
  Cell,
  LabelList,
  XAxis,
  YAxis,
} from 'recharts'

import { cn } from '@/lib/cn'

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart'

export type BarChartLabelConfig = {
  labelPosition?: 'insideTop' | 'top'
  labelClassName?: string
  labelOffset?: number
}

export type BarChartProps = {
  id?: string
  config: ChartConfig<BarChartLabelConfig>
  data: {
    label: string
    value: number
    fill?: string
  }[]
  tooltip?: boolean
  className?: string
  xAxisInterval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd'
  xAxisAngle?: number
  xAxisMinTickGap?: number
  xAxisTickFormatter?: (value: string, index: number) => string
}

const BarChart = ({
  id,
  config,
  data,
  tooltip = true,
  className,
  xAxisInterval,
  xAxisAngle,
  xAxisMinTickGap,
  xAxisTickFormatter,
}: BarChartProps) => {
  const colorConfig = config.value as (typeof config)['value'] &
    BarChartLabelConfig

  const hasCustomFill = data.some((item) => item.fill)

  const computedLabelPosition: 'insideTop' | 'top' =
    colorConfig?.labelPosition ?? 'insideTop'
  const computedLabelClassName =
    colorConfig?.labelClassName ??
    'fill-system-neutral-white text-xs font-medium'
  const computedLabelOffset = colorConfig?.labelOffset ?? 0

  const bottomMargin = xAxisAngle ? 50 : 20

  return (
    <ChartContainer
      id={id}
      config={config}
      className={cn('h-auto min-h-24 w-full', className)}
    >
      <BarCharRecharts
        accessibilityLayer
        data={data}
        margin={{
          top: 20,
          bottom: bottomMargin,
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          tickMargin={xAxisAngle ? 12 : 8}
          axisLine={false}
          interval={xAxisInterval}
          angle={xAxisAngle}
          minTickGap={xAxisMinTickGap}
          tickFormatter={xAxisTickFormatter}
        />
        <YAxis
          dataKey="value"
          tickLine={false}
          tickMargin={12}
          axisLine={false}
        />

        {tooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
        )}

        {colorConfig?.chartClassName ? (
          <Bar
            dataKey="value"
            radius={4}
            className={cn(colorConfig.chartClassName)}
          >
            <LabelList
              position={computedLabelPosition}
              offset={computedLabelOffset}
              className={computedLabelClassName}
            />
            {hasCustomFill &&
              data.map((entry) => (
                <Cell key={`cell-${entry.label}`} fill={entry.fill} />
              ))}
          </Bar>
        ) : (
          <Bar dataKey="value" radius={4} fill="var(--color-value)">
            <LabelList
              position={computedLabelPosition}
              offset={computedLabelOffset}
              className={computedLabelClassName}
            />
            {hasCustomFill &&
              data.map((entry) => (
                <Cell key={`cell-${entry.label}`} fill={entry.fill} />
              ))}
          </Bar>
        )}
      </BarCharRecharts>
    </ChartContainer>
  )
}
BarChart.displayName = 'BarChart'

export { BarChart }
